/**
 * Toile Product Configuration
 *
 * The third product: general-purpose image generation one twists from a preset or from
 * scratch — `continue(basis, direction)`. This file is the ground only. Copy, funnel overrides
 * and commerce arrive with the surfaces; what is here is what root.tsx reads to render a
 * document at all.
 *
 * Identity: thinking/design/toile/toile_identity.md (status: current-canon). Dark is a
 * commitment — there is no light mode, so colorScheme is 'dark' with no counterpart.
 */

import type {FunnelOverrides} from '@core/products/funnel'
import {surfaceOgImageUrl} from '@core/og/cardUrls'

export const toileConfig = {
  id: 'toile',
  name: 'toile',
  domain: 'toile.pictures',

  // SEO & Meta — deliberately spare. The operator authors product copy; a placeholder
  // paragraph written here would be the version that ships by accident.
  meta: {
    title: 'toile',
    description: 'Image generation you twist — from a preset, or from nothing.',
    url: 'https://toile.pictures',
    keywords: 'AI image generation, image generator, AI art',
    ogImage: surfaceOgImageUrl('https://toile.pictures', 'home'),
    ogImageType: 'image/png' as const,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    themeColor: '#141210', // Stage — the ground token
    colorScheme: 'dark' as const,
  },

  // Backend Services — internal service addresses. Server-only: @product/toile/config.server.ts
  // (toileServerConfig.services, full history comment there). Never on this object —
  // toile/config.ts is imported directly by client components (Footer, …) and this whole module
  // is serialized into every page's hydration script by root.tsx's loader.

  // Feature Flags
  features: {
    streamingGeneration: false,
    examples: false,
  },

  // Free tools (`@core/tools`): anonymous runs per tool before sign-in is asked, the same allowance
  // narcis gives.
  freeToolUses: 5,

  // Contact
  contact: {
    email: 'contact@narcis.ai',
  },

  // Funnel — inherits every default from core/products/funnel.ts. Nothing to override until
  // there is a gate to word.
  funnel: {} satisfies FunnelOverrides,

  // Session cookie domain and the commerce processor flag are server-only:
  // @product/toile/config.server.ts (toileServerConfig.session / .commerce), same reason as
  // `services` above.
} as const

export type ToileConfig = typeof toileConfig

/**
 * Base URL for pack sample images (originals). Same shape as `NARCIS_PACK_SAMPLES_BASE_URL` and
 * `OVID_PACK_SAMPLES_BASE_URL`: `<base>/<slug>/<file>.webp`. The toile.pictures CloudFront
 * distribution (`cloudfront-toile.tf`) routes `/pack-samples/*` to the shared content bucket by OAC,
 * same-origin, so this resolves in both dev and prod without an env override. The shared
 * `pack-samples` prefix (narcis + ovid) is never written to from here — see the operator note in
 * `thinking/threads/general_generator.md`.
 */
export const TOILE_PACK_SAMPLES_BASE_URL =
    process.env.TOILE_PACK_SAMPLES_BASE_URL || 'https://toile.pictures/pack-samples/toile'
